import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, IconButton, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const INSURER_ID = 'pacific_cross_primary_care_plan';
const BRAND_COLOR = '#1565c0';
const PREMIUM_ROW_COLOR = '#ffcc80';
const SELECTED_PREMIUM_ROW_COLOR = '#ffb74d';

// Core and Core_wOP are quoted inpatient-only tiers on the rate sheet - Pulse and Complete
// stack more benefits on top. Only one tier is billed at a time.
const PLANS = [
    { id: 'pacific_cross_primary_care_plan_core', key: 'core', label: 'Core' },
    { id: 'pacific_cross_primary_care_plan_core_with_op', key: 'core_with_op', label: 'Core_wOP' },
    { id: 'pacific_cross_primary_care_plan_pulse', key: 'pulse', label: 'Pulse' },
    { id: 'pacific_cross_primary_care_plan_complete', key: 'complete', label: 'Complete' },
];

// Field names match the rate sheet's response fields - optical is quoted under 'vision'.
const OPTIONAL_BENEFITS = [
    { key: 'maternity_5000', label: 'Maternity 5K', premiumField: 'maternity_5000_premium' },
    { key: 'maternity_10000', label: 'Maternity 10K', premiumField: 'maternity_10000_premium' },
    { key: 'dental', label: 'Dental Care', premiumField: 'dental_premium' },
    { key: 'vision', label: 'Optical Care', premiumField: 'optical_premium' },
];

// Every tier plus the optional benefits — all are kept so switching tier / optional
// benefits needs no refetch
const RATE_FIELDS = [...PLANS.map(p => p.key), ...OPTIONAL_BENEFITS.map(b => b.key)];

const AREA_OF_COVERAGE_WITH_USA = 'Worldwide incl USA';
const AREA_OF_COVERAGE_WITHOUT_USA = 'Worldwide excl USA';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency = 'USD') {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isRateAvailable(rate) {
    return typeof rate === 'number' && !isNaN(rate);
}

// Same as PacificCrossPrimaryCarePlan_Rates' getRenewalPercent
function getRenewalPercent(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function PacificCrossPrimaryCarePlan_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const config = appConfig?.content?.specifics?.[INSURER_ID] || {};
    const familyDiscounts = config['Family Discounts'] || {};
    const paymentFrequencyCharges = config['Payment Frequency Charges'] || {};
    const deductibleOptions = appConfig?.content?.deductibles?.[INSURER_ID]?.['USD'] || [];

    // The rate provider only quotes Annually - every other frequency is derived on the
    // frontend from the surcharge the rate sheet's config carries.
    const otherPaymentFrequencies = Object.keys(paymentFrequencyCharges).filter(f => f !== 'Annually');
    const paymentFrequencyItems = ['Annually', ...(otherPaymentFrequencies.length > 0 ? otherPaymentFrequencies : ['Semi Annually'])];

    // Semi-private room is a Hong Kong specific option - every other residence is quoted Private.
    const hasHongKongClient = (clients || []).some(client => client.country_of_residence === 'Hong Kong');

    const [selectedPlan, setSelectedPlan] = useState('pacific_cross_primary_care_plan_core');
    const [optionalBenefits, setOptionalBenefits] = useState({
        maternity_5000: false,
        maternity_10000: false,
        dental: false,
        vision: false,
    });
    const [areaOfCoverage, setAreaOfCoverage] = useState(AREA_OF_COVERAGE_WITHOUT_USA);
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [roomType, setRoomType] = useState('Private');
    const [highCostCountryPlan, setHighCostCountryPlan] = useState(false);
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
    const activeOptionalBenefits = OPTIONAL_BENEFITS.filter(b => optionalBenefits[b.key]);

    // Core and Core_wOP are not quoted at a Nil deductible on the rate sheet.
    function getDeductibleItemsForPlan(planKey) {
        if (planKey === 'core' || planKey === 'core_with_op') {
            return deductibleOptions.filter(d => d !== 'Nil');
        }
        return deductibleOptions;
    }

    function getDefaultQuoteDeductibles(planKey) {
        const items = getDeductibleItemsForPlan(planKey);
        const defaults = {};
        (quotes || []).forEach(q => { defaults[q.name] = items[0] || ''; });
        return defaults;
    }

    function getFamilyDiscountRate() {
        const count = clients.length;
        if (count === 0) return 0;
        if (count >= 4) return familyDiscounts['>4'] || 0;
        return familyDiscounts[count] || 0;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            const savedPlanObj = PLANS.find(p => p.id === editingPlan.plan_id) || PLANS[0];
            setSelectedPlan(savedPlanObj.id);
            const activeBenefits = editingPlan.payloads?.optional_benefits || [];
            setOptionalBenefits({
                maternity_5000: activeBenefits.includes('maternity_5000'),
                maternity_10000: activeBenefits.includes('maternity_10000'),
                dental: activeBenefits.includes('dental'),
                vision: activeBenefits.includes('vision'),
            });
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || AREA_OF_COVERAGE_WITHOUT_USA);
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setRoomType(editingPlan.payloads?.room_type || 'Private');
            setHighCostCountryPlan(!!editingPlan.payloads?.high_cost_country_plan);
            setDiscount(String(editingPlan.payloads?.discount ?? '0'));
            setLoadingPercent(String(editingPlan.payloads?.loading ?? '0'));

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);
            setDeductibleErrors({});

            // Reconstruct ratesData from stored per-field premiums (saved before any discount)
            // — only the saved tier's rate is recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return {
                        [savedPlanObj.key]: premium?.tier_premium ?? null,
                        ...Object.fromEntries(OPTIONAL_BENEFITS.map(b => [b.key, premium?.[b.premiumField] ?? null])),
                    };
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open && !editingPlan) {
            setSelectedPlan('pacific_cross_primary_care_plan_core');
            setOptionalBenefits({ maternity_5000: false, maternity_10000: false, dental: false, vision: false });
            setAreaOfCoverage(AREA_OF_COVERAGE_WITHOUT_USA);
            setPaymentFrequency('Annually');
            setRoomType('Private');
            setHighCostCountryPlan(false);
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles('core'));
            setDeductibleErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // A Hong Kong client can be removed after Semi-Private Room was picked - fall back to Private.
    useEffect(() => {
        if (!hasHongKongClient && roomType === 'SPR') {
            setRoomType('Private');
            invalidateRates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasHongKongClient]);

    // Every tier's rate comes back in one fetch, so switching just shows a different row.
    // Core and Core_wOP aren't quoted at Nil though - a quote on Nil moves to the new tier's
    // first deductible, and since the deductible is part of the request that needs a refetch.
    function handlePlanChange(planId) {
        const nextPlan = PLANS.find(p => p.id === planId) || PLANS[0];
        setSelectedPlan(nextPlan.id);
        const validItems = getDeductibleItemsForPlan(nextPlan.key);
        const needsReset = (quotes || []).some(q => {
            const value = quoteDeductibles[q.name];
            return value && value !== LEAVE_BLANK && !validItems.includes(value);
        });
        if (!needsReset) return;
        setQuoteDeductibles(prev => {
            const next = { ...prev };
            (quotes || []).forEach(q => {
                const value = prev[q.name];
                if (value && value !== LEAVE_BLANK && !validItems.includes(value)) next[q.name] = validItems[0] || '';
            });
            return next;
        });
        invalidateRates();
    }

    // Optional benefit rates come back with the tier rates, so toggling one just shows a
    // different set of rows — no refresh needed.
    function toggleOptionalBenefit(key) {
        setOptionalBenefits(prev => {
            const next = { ...prev, [key]: !prev[key] };
            if (key === 'maternity_5000' && next.maternity_5000) next.maternity_10000 = false;
            if (key === 'maternity_10000' && next.maternity_10000) next.maternity_5000 = false;
            return next;
        });
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => {
                    if (isBlankQuote(quote)) return Promise.resolve({ quoteUid: quote.uid, blank: true });
                    // The rate provider always prices every tier in one call and indexes
                    // deductibles by tier key directly (no fallback), so every key must be
                    // present even though only the selected tier's rate is used here.
                    const deductibleValue = quoteDeductibles[quote.name] || '';
                    const specificDeductibles = {
                        core: deductibleValue,
                        core_with_op: deductibleValue,
                        pulse: deductibleValue,
                        complete: deductibleValue,
                    };

                    return getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { [INSURER_ID]: specificDeductibles },
                            insurers: [INSURER_ID],
                            specifics: {
                                [INSURER_ID]: {
                                    deductibles: specificDeductibles,
                                    room_type: roomType,
                                    high_cost_country_plan: highCostCountryPlan,
                                },
                            },
                            specific_area_of_coverage: { [INSURER_ID]: areaOfCoverage },
                            rates_version_per_insurer: { [INSURER_ID]: selectedRatesVersion?.[INSURER_ID] || '' },
                            specific_currency: { [INSURER_ID]: 'USD' },
                            specific_payment_frequency: { [INSURER_ID]: 'Annually' },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.[INSURER_ID] }));
                })
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Pacific Cross PrimaryCare Plan rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    function getRawRate(clientIndex, key, quoteUid) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw) return null;
        const val = raw[key];
        return val === undefined ? null : val;
    }

    function getTierRate(clientIndex, quoteUid) {
        return getRawRate(clientIndex, selectedPlanObj.key, quoteUid);
    }

    // Family Discount + Renewal Discount/Loading only scale the tier premium - optional
    // benefits are flat rates added on top. Same as PacificCrossPrimaryCarePlan_Rates.
    function getDiscountFactor() {
        return (1 - getFamilyDiscountRate()) * getRenewalPercent(discount, loadingPercent);
    }

    function getTierRateAfterDiscount(clientIndex, quoteUid) {
        const rate = getTierRate(clientIndex, quoteUid);
        if (!isRateAvailable(rate)) return rate;
        return rate * getDiscountFactor();
    }

    function getOptionalPremium(clientIndex, quoteUid) {
        let total = 0;
        activeOptionalBenefits.forEach(({ key }) => {
            const rate = getRawRate(clientIndex, key, quoteUid);
            if (isRateAvailable(rate)) total += rate;
        });
        return total;
    }

    function getFinalPremium(clientIndex, quoteUid) {
        const tierRate = getTierRateAfterDiscount(clientIndex, quoteUid);
        if (!isRateAvailable(tierRate)) return tierRate;
        return tierRate + getOptionalPremium(clientIndex, quoteUid);
    }

    function getSubtotal(getRateForClient) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRateForClient(i);
            if (isRateAvailable(rate)) { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Annually is the only frequency the rate provider quotes - every other frequency is
    // derived here from the surcharge the rate sheet's config carries.
    function getPremiumPerPaymentFrequency(frequency, quoteUid, clientIndex = null) {
        const annual = clientIndex === null
            ? getSubtotal((idx) => getFinalPremium(idx, quoteUid))
            : getFinalPremium(clientIndex, quoteUid);
        if (!isRateAvailable(annual)) return annual;

        const surcharge = paymentFrequencyCharges[frequency] || 0;
        if (frequency === 'Semi Annually') {
            return annual + (annual * surcharge);
        }
        const installments = { 'Annually': 1, 'Quarterly': 4, 'Monthly': 12 }[frequency] || 1;
        return (annual + (annual * surcharge)) / installments;
    }

    function getPaymentFrequencyLabel(frequency) {
        if (frequency === 'Annually') return 'Final Annual Premium';
        const percent = ((paymentFrequencyCharges[frequency] || 0) * 100).toFixed(0);
        if (frequency === 'Semi Annually') return `Semi Annual Premium (${percent}%)`;
        return `${frequency} Premium (+${percent}%)`;
    }

    function buildPlanLabel() {
        if (activeOptionalBenefits.length === 0) return selectedPlanObj.label;
        return `${selectedPlanObj.label} + ${activeOptionalBenefits.map(b => b.label).join(' + ')}`;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const activeKeys = activeOptionalBenefits.map(b => b.key);
        const effectivePlanId = editingPlan ? editingPlan.plan_id : selectedPlan;
        // plan_id identifies the tier and is shared by every instance of it; uid identifies this
        // specific plan instance so multiple plans of the same tier can coexist instead of
        // overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();

        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getPremiumPerPaymentFrequency(paymentFrequency, quote.uid, clientIndex);

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: INSURER_ID,
                        plan: selectedPlanObj.key,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: quoteDeductibles[quote.name] || '',
                        area_of_coverage: areaOfCoverage,
                        room_type: roomType,
                        high_cost_country_plan: highCostCountryPlan,
                        currency: 'USD',
                        payment_frequency: paymentFrequency,
                        optional_benefits: activeKeys,
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        // Component premiums are the rate sheet values before any discount
                        tier_premium: getTierRate(clientIndex, quote.uid),
                        ...Object.fromEntries(OPTIONAL_BENEFITS.map(b => [b.premiumField, getRawRate(clientIndex, b.key, quote.uid)])),
                    },
                });
            });
        });

        const plan = {
            plan_id: effectivePlanId,
            uid: planUid,
            plan: selectedPlanObj.key,
            insurer: INSURER_ID,
            label: `Pacific Cross PrimaryCare Plan ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                payment_frequency: paymentFrequency,
                room_type: roomType,
                high_cost_country_plan: highCostCountryPlan,
                optional_benefits: activeKeys,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const familyDiscountRate = getFamilyDiscountRate();
    const discountFactor = getDiscountFactor();
    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                }}>Pacific Cross PrimaryCare Plan</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 4, overflow: 'hidden' }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
                            alt="Pacific Cross"
                            style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ width: 170 }}>
                        <Select
                            value={selectedPlan}
                            disabled={loading || !!editingPlan}
                            onChange={(e) => handlePlanChange(e.target.value)}
                            sx={selectSx}
                        >
                            {PLANS.map(plan => (
                                <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Optional Benefits — same as PacificCrossPrimaryCarePlan_Rates */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ fontSize: 15 }}>Optional Benefits</Typography>
                    {OPTIONAL_BENEFITS.map(benefit => (
                        <FormControlLabel
                            key={benefit.key}
                            sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={optionalBenefits[benefit.key]}
                                    onChange={() => toggleOptionalBenefit(benefit.key)}
                                    disabled={loading}
                                />
                            }
                            label={benefit.label}
                        />
                    ))}
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Semi-Private Room (HK)</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={hasHongKongClient && roomType === 'SPR' ? 'Yes' : 'No'}
                            onChange={(e) => { setRoomType(e.target.value === 'Yes' ? 'SPR' : 'Private'); invalidateRates(); }}
                            items={['No', 'Yes']}
                            disabled={loading || !hasHongKongClient}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>High-Cost Country Plan</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={highCostCountryPlan ? 'Yes' : 'No'}
                            onChange={(e) => { setHighCostCountryPlan(e.target.value === 'Yes'); invalidateRates(); }}
                            items={['No', 'Yes']}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area of Coverage</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={areaOfCoverage}
                            onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                            items={[AREA_OF_COVERAGE_WITHOUT_USA, AREA_OF_COVERAGE_WITH_USA]}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                // Derived from the annual premium on the frontend — no refetch
                                onChange={(e) => setPaymentFrequency(e.target.value)}
                                sx={selectSx}
                            >
                                {paymentFrequencyItems.map(frequency => (
                                    <MenuItem key={frequency} value={frequency} sx={menuItemSx}>{frequency}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Family Discount</Typography>
                        <Typography sx={{ fontSize: 13, color: BRAND_COLOR, fontWeight: 600 }}>{(familyDiscountRate * 100).toFixed(0)}%</Typography>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductible */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductible per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...getDeductibleItemsForPlan(selectedPlanObj.key)]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Tier premium, before any discount */}
                                            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                <TableCell sx={{ color: 'text.secondary' }}>{selectedPlanObj.label}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        {formatRate(getTierRate(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {formatRate(getSubtotal((idx) => getTierRate(idx, quote.uid)))}
                                                </TableCell>
                                            </TableRow>

                                            {/* Tier premium after Family Discount / Renewal Discount & Loading */}
                                            {discountFactor !== 1 && (
                                                <TableRow sx={{ backgroundColor: PREMIUM_ROW_COLOR }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Premium after discount</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getTierRateAfterDiscount(idx, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getSubtotal((idx) => getTierRateAfterDiscount(idx, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Optional benefits - flat rates, not subject to any discount */}
                                            {activeOptionalBenefits.length > 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={clients.length + 2} sx={{ fontWeight: 600, background: BRAND_COLOR, color: '#fff' }}>Optional Premium</TableCell>
                                                </TableRow>
                                            )}
                                            {activeOptionalBenefits.map((benefit, benefitIdx) => (
                                                <TableRow key={benefit.key} sx={{ backgroundColor: benefitIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{benefit.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getRawRate(idx, benefit.key, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getSubtotal((idx) => getRawRate(idx, benefit.key, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Premium per payment frequency — same rows as PacificCrossPrimaryCarePlan_Rates;
                                                the selected Payment Frequency is the one saved */}
                                            {paymentFrequencyItems.map(frequency => {
                                                const isSelected = frequency === paymentFrequency;
                                                return (
                                                    <TableRow key={frequency} sx={{ backgroundColor: isSelected ? SELECTED_PREMIUM_ROW_COLOR : PREMIUM_ROW_COLOR }}>
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 600 }}>{getPaymentFrequencyLabel(frequency)}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx} sx={{ fontWeight: isSelected ? 700 : 400 }}>
                                                                {formatRate(getPremiumPerPaymentFrequency(frequency, quote.uid, idx))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>
                                                            {formatRate(getPremiumPerPaymentFrequency(frequency, quote.uid))}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId={INSURER_ID} />

                <RatesVersionSelect
                    insurer={INSURER_ID}
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
